/**
 * Zod schema for "Add Azure DevOps Connection" (Charter M3 migration), imported by
 * `AzureDevOpsConnectionForm.tsx`, never redefined (form-standard.context.md §3). Rule IDs `VAD-*`
 * mirror the pre-migration hand-rolled `validate()` exactly, including the domain-substring check
 * that only fires once the URL is independently confirmed parseable.
 */
import { z } from 'zod';
import type { AzureDevOpsConnection, CreateAzureDevOpsConnectionRequest } from '../types';
import { AZURE_DEVOPS_AUTH_TYPE_OPTIONS, isParseableUrl } from './connectionFormFields';

// reason: AZURE_DEVOPS_AUTH_TYPE_OPTIONS is a runtime-verified non-empty array of the exported
// union type; zod's z.enum() requires a literal non-empty tuple type.
const AUTH_TYPE_TUPLE = AZURE_DEVOPS_AUTH_TYPE_OPTIONS.map((o) => o.value) as [
  AzureDevOpsConnection['authType'],
  ...AzureDevOpsConnection['authType'][],
];

function isAzureDevOpsDomain(value: string): boolean {
  return value.includes('dev.azure.com') || value.includes('.visualstudio.com');
}

export const AzureDevOpsConnectionSchema = z.object({
  // VAD-01
  name: z.string().trim().min(1, 'Connection name is required'),
  // VAD-02 (required) / VAD-03 (must be a parseable URL) / VAD-04 (must be an Azure DevOps
  // organization URL — only evaluated once the URL is independently parseable, matching the
  // original's `try { new URL(...); if (!includes(...)) {...} } catch {...}` nesting).
  organizationUrl: z
    .string()
    .trim()
    .min(1, 'Organization URL is required')
    .refine(isParseableUrl, { message: 'Please enter a valid URL' })
    .refine((v) => !isParseableUrl(v) || isAzureDevOpsDomain(v), {
      message: 'URL must be an Azure DevOps organization URL',
    }),
  authType: z.enum(AUTH_TYPE_TUPLE),
  // VAD-05
  accessToken: z.string().trim().min(1, 'Access token is required'),
});

/** The shape RHF's `useForm<T>`/`defaultValues` are typed against (pre-transform). */
export type AzureDevOpsConnectionFormValues = z.input<typeof AzureDevOpsConnectionSchema>;
/** The shape `handleSubmit(onValid)` receives (post-transform — trimmed). */
export type AzureDevOpsConnectionFormOutput = z.output<typeof AzureDevOpsConnectionSchema>;

// Compile-time proof the schema's output assembles a valid request DTO.
type _AssertAssignable = AzureDevOpsConnectionFormOutput extends CreateAzureDevOpsConnectionRequest ? true : false;
const _assertAssignable: _AssertAssignable = true;
void _assertAssignable;
